import { useCallback, useMemo, useState } from 'react';

import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import { attendanceMarksService } from '@/api/domains/attendance/services/attendanceMarks.service';
import { formatDateToYYYYMMDD, calculateDateRange } from '@/utils';
import { downloadBlob } from '@/utils/blobDownload';
import type {
  AttendanceMark,
  AttendanceMarkStatus,
  EmployeeStatusFilter,
  LC_ExportAttendanceMarksIn,
} from '@/api/domains/attendance/types/attendanceMarks.types';
import type { LC_ValidationError } from '@/api/shared';
import type { AttendanceFiltersResult } from '@/hooks/attendance/common/useAttendanceFiltersPanel';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table.types';


const DEFAULT_PAGE_SIZE = 25;

function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
  fullName: 'fullName',
  date: 'markDate',
  time: 'markTime',
  locationName: 'ubicationName',
  eventName: 'eventName',
  status: 'markStatus',
  modifiedDate: 'modifiedDate',
  modifiedByName: 'modifiedByName',
};

interface FilterState {
  employeeIds: number[];
  eventIds: number[];
  dateFrom: string;
  dateTo: string;
  status: AttendanceMarkStatus | undefined;
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  locationIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeStatusFilter;
}

export interface UseAttendanceMarksAdminOptions {
  onValidationErrors?: (errors: LC_ValidationError[]) => void;
}

export interface UseAttendanceMarksAdminResult {
  rows: AttendanceMark[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<AttendanceMark>[];
  actions: TableAction<AttendanceMark>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  onFiltersApply: (result: AttendanceFiltersResult) => void;
  onExport: () => Promise<void>;
  isExporting: boolean;
}

export function useAttendanceMarksAdmin(
  options: UseAttendanceMarksAdminOptions = {},
): UseAttendanceMarksAdminResult {
  const { onValidationErrors } = options;
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showNotification, openModal, closeModal } = useUI();
  const { canEdit } = usePagePermissions();

  // Lazy initial filters: calculateDateRange is invoked on first render only,
  // not at module load — keeps unrelated test files that partially mock
  // `@/utils` from breaking when this module is transitively imported.
  const initialFilters = useMemo<FilterState>(() => {
    const defaultRange = calculateDateRange('MONTH_1', formatDateToYYYYMMDD);
    return {
      employeeIds: [],
      eventIds: [],
      dateFrom: defaultRange.dateFrom,
      dateTo: defaultRange.dateTo,
      status: 1 as AttendanceMarkStatus,
      companyIds: [],
      areaIds: [],
      positionIds: [],
      costCenterIds: [],
      locationIds: [],
      contractModalityIds: [],
      groupIds: [],
      employeeStatus: 'vigente',
    };
  }, []);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FilterState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AttendanceMark> | null> => {
      const employeeIdsParam = params.filters.employeeIds.length > 0
        ? params.filters.employeeIds.join(',')
        : undefined;

      const eventIdsParam = params.filters.eventIds.length > 0
        ? params.filters.eventIds.join(',')
        : undefined;

      const response = await attendanceMarksService.getMarks(
        {
          page: params.page,
          pageSize: params.pageSize,
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          employeeIds: employeeIdsParam,
          eventIds: eventIdsParam,
          status: params.filters.status,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
          companyIds: joinOrUndefined(params.filters.companyIds),
          areaIds: joinOrUndefined(params.filters.areaIds),
          positionIds: joinOrUndefined(params.filters.positionIds),
          costCenterIds: joinOrUndefined(params.filters.costCenterIds),
          locationIds: joinOrUndefined(params.filters.locationIds),
          contractModalityIds: joinOrUndefined(params.filters.contractModalityIds),
          groupIds: joinOrUndefined(params.filters.groupIds),
          employeeStatus: params.filters.employeeStatus,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;

        const validationErrors = response.error?.validationErrors;
        if (validationErrors && validationErrors.length > 0 && onValidationErrors) {
          onValidationErrors(validationErrors);
          return null;
        }

        showApiError(response, translate('attendance:marksAdmin.messages.loadError'));
        return null;
      }

      return {
        items: response.data.items,
        totalRecords: response.data.pagination.totalRecords,
      };
    },
    [showApiError, translate, onValidationErrors],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    setFilters,
    filters,
    refetch,
  } = usePaginatedList<AttendanceMark, FilterState>({
    fetchFn,
    initialFilters,
    initialPageSize: DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
    initialSort: { key: 'fullName', direction: 'ASC' },
  });

  const [isExporting, setIsExporting] = useState(false);

  const onExport = useCallback(async () => {
    setIsExporting(true);

    const params: LC_ExportAttendanceMarksIn = {
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      employeeIds: joinOrUndefined(filters.employeeIds.map(String)),
      eventIds: joinOrUndefined(filters.eventIds.map(String)),
      status: filters.status,
      companyIds: joinOrUndefined(filters.companyIds),
      areaIds: joinOrUndefined(filters.areaIds),
      positionIds: joinOrUndefined(filters.positionIds),
      costCenterIds: joinOrUndefined(filters.costCenterIds),
      locationIds: joinOrUndefined(filters.locationIds),
      contractModalityIds: joinOrUndefined(filters.contractModalityIds),
      groupIds: joinOrUndefined(filters.groupIds),
      employeeStatus: filters.employeeStatus,
    };

    const response = await attendanceMarksService.exportFile(params);

    if (!response.success || !response.data) {
      showApiError(response, translate('attendance:marksAdmin.messages.exportError'));
      setIsExporting(false);
      return;
    }

    if (response.data.type === 'sync') {
      downloadBlob(response.data.blob, response.data.filename);
      showSuccess(translate('attendance:marksAdmin.messages.exportSuccess'));
    } else {
      // 202: el backend genera el archivo en background y notifica al usuario por email.
      showNotification('info', translate('attendance:marksAdmin.messages.exportQueued'), 8000);
    }

    setIsExporting(false);
  }, [filters, showApiError, showSuccess, showNotification, translate]);

  const columns: TableColumn<AttendanceMark>[] = useMemo(() => [
    {
      key: 'rut',
      header: translate('attendance:marksAdmin.columns.rut'),
      sortable: true,
    },
    {
      key: 'fullName',
      header: translate('attendance:marksAdmin.columns.employee'),
      sortable: true,
    },
    {
      key: 'date',
      header: translate('attendance:marksAdmin.columns.date'),
      sortable: true,
    },
    {
      key: 'time',
      header: translate('attendance:marksAdmin.columns.time'),
      sortable: true,
    },
    {
      key: 'locationName',
      header: translate('attendance:marksAdmin.columns.location'),
      sortable: true,
    },
    {
      key: 'eventName',
      header: translate('attendance:marksAdmin.columns.event'),
      sortable: true,
    },
    {
      key: 'status',
      header: translate('attendance:marksAdmin.columns.status'),
      sortable: true,
      render: (row: AttendanceMark) => (
        <Text variant="subheader-regular" as="span">
          {translate(
            row.isEnabled
              ? 'attendance:marksAdmin.statuses.enabled'
              : 'attendance:marksAdmin.statuses.disabled',
          )}
        </Text>
      ),
    },
    {
      key: 'modifiedDate',
      header: translate('attendance:marksAdmin.columns.modifiedDate'),
      sortable: true,
    },
    {
      key: 'modifiedByName',
      header: translate('attendance:marksAdmin.columns.modifiedByName'),
      sortable: true,
    },
  ], [translate]);

  const handleToggle = useCallback((row: AttendanceMark) => {
    const isEnabling = !row.isEnabled;
    const titleKey = isEnabling
      ? 'attendance:marksAdmin.toggle.enableTitle'
      : 'attendance:marksAdmin.toggle.disableTitle';
    const bodyKey = isEnabling
      ? 'attendance:marksAdmin.toggle.enableBody'
      : 'attendance:marksAdmin.toggle.disableBody';

    openModal('ConfirmModal', {
      variant: isEnabling ? 'success' : 'error',
      icon: isEnabling ? 'check' : 'close',
      iconColor: isEnabling ? colors.important.main : undefined,
      confirmButtonVariant: 'alert',
      title: translate(titleKey),
      bodyText: translate(bodyKey, {
        employee: row.fullName,
        date: row.date,
        time: row.time,
      }),
      confirmText: translate('common:actions.confirm'),
      cancelText: translate('common:actions.cancel'),
      onConfirm: async () => {
        const response = await attendanceMarksService.toggleMarkEnabled(row.id, isEnabling);
        if (!response.success) {
          showApiError(response, translate('attendance:marksAdmin.toggle.error'));
          return;
        }
        const message = response.data?.message || translate('attendance:marksAdmin.toggle.success');
        showNotification('info', message);
        closeModal('ConfirmModal');
        refetch();
      },
      onCancel: () => closeModal('ConfirmModal'),
    });
  }, [openModal, closeModal, refetch, showApiError, showNotification, translate]);

  const actions: TableAction<AttendanceMark>[] = useMemo(() => [
    {
      id: 'enable',
      label: translate('attendance:marksAdmin.actions.enable'),
      icon: 'check',
      color: colors.important.main,
      onClick: handleToggle,
      hidden: (row: AttendanceMark) => row.isEnabled || !canEdit,
    },
    {
      id: 'disable',
      label: translate('attendance:marksAdmin.actions.disable'),
      icon: 'close',
      color: colors.feedback.error300,
      onClick: handleToggle,
      hidden: (row: AttendanceMark) => !row.isEnabled || !canEdit,
    },
  ], [handleToggle, canEdit, translate]);

  const onFiltersApply = useCallback((result: AttendanceFiltersResult) => {
    const status: AttendanceMarkStatus | undefined =
      result.markStatus === 0 || result.markStatus === 1 ? result.markStatus : undefined;
    setFilters({
      employeeIds: result.employeeIds,
      eventIds: result.eventIds,
      dateFrom: result.dateFrom,
      dateTo: result.dateTo,
      status,
      companyIds: result.companyIds,
      areaIds: result.areaIds,
      positionIds: result.positionIds,
      costCenterIds: result.costCenterIds,
      locationIds: result.locationIds,
      contractModalityIds: result.contractModalityIds,
      groupIds: result.groupIds,
      employeeStatus: result.employeeStatus,
    });
  }, [setFilters]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    isLoading: loading,
    onFiltersApply,
    onExport,
    isExporting,
  };
}
